import {
  AvailableLabel,
  Container,
  Employee,
  IconNames,
  TerminologyOptions,
} from "@/types/v2";
import { Button } from "@/components/Base/Button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/Base/Select";
import { PlusIcon, X } from "lucide-react";
import { Label } from "@/components/Base/Label";
import { Tooltip } from "@/components/Common/Tooltip";
import { getEmployeeName } from "@/lib/employees";
import { getInitials } from "@/lib/utils";
import { Avatar } from "@/components/Common/Avatar";
import { Icon } from "@/components/Common/Icon";
import { Spinner } from "@/components/Common/Spinner";
import { z, ZodObject, ZodRawShape } from "zod";
import { useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/Base/Form";
import { Input } from "@/components/Base/Input";
import { Switch } from "@/components/Base/Switch";
import { useTerminologyReplacer } from "@/hooks/useTerminologyReplacer";
import { useFeature } from "@/hooks/useFeatures";
import {
  normalizeLabels,
  normalizeTechs,
  getBidirectionalTechnicianIds,
} from "@/lib/checkContainerConfiguration";
import { labelHasCatalogMatch } from "@/lib/labelHasCatalogMatch";
import { sortOptionsByLabel } from "@/lib/sortOptionsByLabel";
import {
  ContainerLinkGroup,
  ContainerLinkGroupRow,
} from "@/components/Layout/ContainerLinkGroup";

export interface ContainerFormProps<T> {
  containerData?: T;
  onSubmit: (container: T) => Promise<void>;
  onCancel?: () => void;
  employees?: Employee[];
  labels?: string[];
  labelCatalog?: AvailableLabel[];
  loading?: boolean;
  containers?: Container[];
  /** Display name of the shop's connected integration (e.g. "Tekmetric").
   * Omit for a manual-only shop -- every two-way-sync affordance (both
   * groups' Switch columns and the footer summary) is hidden entirely. */
  integrationName?: string;
  /** Shop-wide technician-writeback setting (isTechnicianWritebackEnabledForShop).
   * Gates whether the per-technician sync Switch is interactive at all
   * (WEBV3-302) -- when true, each row's Switch reflects and edits that
   * technician's own bidirectionalTechnicianId membership on this
   * container; when false, every row shows disabled/unchecked regardless
   * of that container's stored bidirectionalTechnicianId. */
  technicianWritebackEnabled?: boolean;
}

const shape: ZodRawShape = {};

export type ContainerFormValues = Pick<Container, "displayName"> & {
  id?: string;
  linkedTechnicianId?: string[];
  linkedSourceLabel?: string[];
  bidirectionalLabel?: string[];
  bidirectionalTechnicianId?: string[];
};

/**
 * Converts a stored Container into the shape ContainerForm edits: linked
 * technician/label fields normalized to arrays regardless of whether
 * Firestore holds them as a bare string, a number, or an array.
 */
export const toContainerFormValues = (
  container: Container
): ContainerFormValues => ({
  ...container,
  linkedTechnicianId: normalizeTechs(container.linkedTechnicianId),
  linkedSourceLabel: normalizeLabels(container.linkedSourceLabel),
  bidirectionalLabel: normalizeLabels(container.bidirectionalLabel),
  bidirectionalTechnicianId: getBidirectionalTechnicianIds(container),
});

const defaultValues: ContainerFormValues = {
  displayName: "",
  linkedSourceLabel: [],
  linkedTechnicianId: [],
  bidirectionalLabel: [],
  bidirectionalTechnicianId: [],
};

export const ContainerForm = <T extends ContainerFormValues>({
  containerData,
  onSubmit,
  onCancel,
  employees,
  labels,
  labelCatalog,
  loading,
  containers,
  integrationName,
  technicianWritebackEnabled,
}: ContainerFormProps<T>) => {
  const { terminolize } = useTerminologyReplacer();
  const isEditing = !!containerData;
  const isROLabelWritebackEnabled = useFeature("isROLabelWritebackEnabled");
  const formDefaultValues = {
    ...defaultValues,
    ...(containerData as T),
    displayName: containerData?.displayName ?? containerData?.id ?? "",
    // `containerData` is not guaranteed to have already gone through
    // toContainerFormValues (production call sites do; some tests pass a
    // raw container-shaped object directly) -- re-derive the effective
    // bidirectional-technician set here too so the backward-compat
    // opt-out default applies regardless.
    bidirectionalTechnicianId: getBidirectionalTechnicianIds(
      containerData as unknown as Container | undefined
    ),
  };

  const existingContainers = containers
    ?.filter((c) => c.id !== containerData?.id)
    .map((c) => (c.displayName ?? c.id).toLowerCase());

  const ContainerSchema: ZodObject<typeof shape> = z.object({
    id: z.string().optional(),
    displayName: z
      .string()
      .min(1, { message: "A name for the container is required" })
      .refine((name) => !existingContainers?.includes(name.toLowerCase()), {
        message:
          "This container name already exists. Please choose a different name.",
      }),
    linkedSourceLabel: z.array(z.string()),
    linkedTechnicianId: z.array(z.string()),
    bidirectionalLabel: z.array(z.string()).optional(),
    bidirectionalTechnicianId: z.array(z.string()).optional(),
    ...shape,
  });

  const { control, handleSubmit, watch, formState, ...form } = useForm<
    z.infer<typeof ContainerSchema>
  >({
    resolver: zodResolver(ContainerSchema),
    defaultValues: formDefaultValues,
  });
  const { setValue } = form;

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "linkedTechnicianId",
  });

  // Watch the current values to disable the add button when needed
  const watchedTechnicianIds = watch("linkedTechnicianId") as string[];
  const hasEmptySelection = watchedTechnicianIds?.some((id) => id === "");

  const {
    fields: linkedSourceLabelField,
    append: linkedSourceLabelAppend,
    remove: linkedSourceLabelRemove,
    update: linkedSourceLabelUpdate,
  } = useFieldArray({
    control,
    name: "linkedSourceLabel",
  });

  // Watch the current values to disable the add button when needed
  const watchedSourceLabels = watch("linkedSourceLabel") as string[];
  const hasEmptySourceLabelSelection = watchedSourceLabels?.some(
    (id) => id === ""
  );
  const watchedBidirectionalLabels =
    (watch("bidirectionalLabel") as string[] | undefined) ?? [];
  const watchedBidirectionalTechnicianIds =
    (watch("bidirectionalTechnicianId") as string[] | undefined) ?? [];

  const handleCancelForm = () => {
    if (onCancel) onCancel();
  };

  const handleUpdateSourceLabel = (index: number, value: string) => {
    if (
      watchedSourceLabels[index] !== "" &&
      watchedBidirectionalLabels.includes(watchedSourceLabels[index])
    ) {
      setValue(
        "bidirectionalLabel",
        watchedBidirectionalLabels.map((l) =>
          l === watchedSourceLabels[index] ? value : l
        )
      );
    }
    linkedSourceLabelUpdate(index, value);
  };

  const handleRemoveSourceLabel = (index: number) => {
    if (watchedBidirectionalLabels.includes(watchedSourceLabels[index])) {
      setValue(
        "bidirectionalLabel",
        watchedBidirectionalLabels.filter(
          (l) => l !== watchedSourceLabels[index]
        )
      );
    }
    linkedSourceLabelRemove(index);
  };

  const handleToggleBidirectional = (label: string, checked: boolean) => {
    setValue(
      "bidirectionalLabel",
      checked
        ? [...watchedBidirectionalLabels, label]
        : watchedBidirectionalLabels.filter((l) => l !== label)
    );
  };

  const handlesSubmitForm = async (values: T) => {
    const filteredSourceLabel = values.linkedSourceLabel?.filter(
      (label) => label !== ""
    );
    const filteredTechnicianId = values.linkedTechnicianId?.filter(
      (id) => id !== ""
    );
    if (onSubmit)
      return onSubmit({
        ...values,
        linkedTechnicianId: filteredTechnicianId,
        linkedSourceLabel: filteredSourceLabel,
        bidirectionalLabel:
          values.bidirectionalLabel?.filter((label) =>
            filteredSourceLabel?.includes(label)
          ) ?? [],
        bidirectionalTechnicianId:
          values.bidirectionalTechnicianId?.filter((id) =>
            filteredTechnicianId?.includes(id)
          ) ?? [],
      });
  };

  const showTechnicianSync = !!integrationName;
  const showLabelSync = !!integrationName && isROLabelWritebackEnabled;

  const technicianRows: ContainerLinkGroupRow[] = fields.map((field, index) => {
    const technicianId = watchedTechnicianIds[index];
    const name = getEmployeeName(technicianId, employees ?? []);
    return {
      key: field.id,
      avatar: (
        <Avatar
          className="h-[26px] w-[26px]"
          fallbackClassName="text-[10px]"
          fallback={getInitials(name)}
        />
      ),
      label: name,
      onRemove: () => handleRemoveTechnician(index),
      removeTooltip: `Remove ${name}`,
      sync: showTechnicianSync
        ? {
            checked: technicianWritebackEnabled
              ? watchedBidirectionalTechnicianIds.includes(technicianId)
              : false,
            disabled: !technicianWritebackEnabled,
            onCheckedChange: technicianWritebackEnabled
              ? (checked: boolean) =>
                  handleToggleBidirectionalTechnician(technicianId, checked)
              : undefined,
            tooltip: technicianWritebackEnabled
              ? `Mark as a technician written back to ${integrationName} on a manual card move`
              : `Turn on technician sync in shop settings to sync back to ${integrationName}`,
          }
        : undefined,
    };
  });

  const technicianAddOptions = sortOptionsByLabel(
    (employees ?? [])
      .filter(
        (emp) =>
          !emp.hideInShopHero && !watchedTechnicianIds?.includes(`${emp.id}`)
      )
      .map((emp) => ({
        value: `${emp.id}`,
        label: `${emp.firstName} ${emp.lastName}`,
      }))
  );

  const handleAddTechnician = (value: string) => {
    if (watchedTechnicianIds?.includes(value)) return;
    append(value);
    // New links default to bidirectional-enabled (opt-out), the reverse of
    // how a newly-added label defaults to NOT bidirectional (opt-in) --
    // see the Container.bidirectionalTechnicianId doc comment for why.
    setValue("bidirectionalTechnicianId", [
      ...watchedBidirectionalTechnicianIds,
      value,
    ]);
  };

  const handleRemoveTechnician = (index: number) => {
    const technicianId = watchedTechnicianIds[index];
    if (watchedBidirectionalTechnicianIds.includes(technicianId)) {
      setValue(
        "bidirectionalTechnicianId",
        watchedBidirectionalTechnicianIds.filter((id) => id !== technicianId)
      );
    }
    remove(index);
  };

  const handleUpdateTechnician = (index: number, value: string) => {
    const previousId = watchedTechnicianIds[index];
    if (
      previousId !== "" &&
      watchedBidirectionalTechnicianIds.includes(previousId)
    ) {
      // Remapping a bidirectional slot's technician can collide with an
      // already-bidirectional entry for `value` elsewhere on this
      // container (e.g. `value` is linked in another slot and already
      // bidirectional) -- dedupe so we never persist a duplicate id.
      setValue(
        "bidirectionalTechnicianId",
        Array.from(
          new Set(
            watchedBidirectionalTechnicianIds.map((id) =>
              id === previousId ? value : id
            )
          )
        )
      );
    } else if (previousId === "") {
      // Filling a freshly-added empty slot defaults to bidirectional-enabled,
      // same opt-out default handleAddTechnician uses.
      setValue("bidirectionalTechnicianId", [
        ...watchedBidirectionalTechnicianIds,
        value,
      ]);
    }
    update(index, value);
  };

  const handleToggleBidirectionalTechnician = (
    technicianId: string,
    checked: boolean
  ) => {
    setValue(
      "bidirectionalTechnicianId",
      checked
        ? [...watchedBidirectionalTechnicianIds, technicianId]
        : watchedBidirectionalTechnicianIds.filter((id) => id !== technicianId)
    );
  };

  const labelRows: ContainerLinkGroupRow[] = linkedSourceLabelField.map(
    (field, index) => {
      const label = watchedSourceLabels[index];
      return {
        key: field.id,
        avatar: (
          <div className="flex h-[26px] w-[26px] items-center justify-center">
            <span className="h-3 w-3 rounded-full bg-primary" />
          </div>
        ),
        label,
        onRemove: () => handleRemoveSourceLabel(index),
        removeTooltip: `Remove ${label}`,
        sync: showLabelSync
          ? {
              checked: watchedBidirectionalLabels.includes(label),
              disabled: !labelHasCatalogMatch(label, labelCatalog),
              onCheckedChange: labelHasCatalogMatch(label, labelCatalog)
                ? (checked: boolean) =>
                    handleToggleBidirectional(label, checked)
                : undefined,
              tooltip: labelHasCatalogMatch(label, labelCatalog)
                ? `Mark as the label written back to ${integrationName} on a manual card move`
                : `No matching ${integrationName} label found — this label can't sync back until it's re-linked to a current ${integrationName} label`,
            }
          : undefined,
      };
    }
  );

  const labelAddOptions = (labels ?? [])
    .filter((label) => !watchedSourceLabels?.includes(label))
    .map((label) => ({ value: label, label }));

  const handleAddLabel = (value: string) => {
    if (watchedSourceLabels?.includes(value)) return;
    linkedSourceLabelAppend(value);
  };

  const linkedTechnicianCount = watchedTechnicianIds?.length ?? 0;
  const linkedLabelCount = watchedSourceLabels?.length ?? 0;
  const syncingTechnicianCount = technicianWritebackEnabled
    ? (watchedTechnicianIds?.filter((id) =>
        watchedBidirectionalTechnicianIds.includes(id)
      ).length ?? 0)
    : 0;
  const syncingLabelCount = showLabelSync
    ? (watchedSourceLabels?.filter((label) =>
        watchedBidirectionalLabels.includes(label)
      ).length ?? 0)
    : 0;
  const totalLinkCount = linkedTechnicianCount + linkedLabelCount;
  const syncCount = syncingTechnicianCount + syncingLabelCount;
  const syncSummaryText = integrationName
    ? syncCount === 0
      ? `Nothing syncs back to ${integrationName}`
      : `${syncCount} of ${totalLinkCount} links sync back to ${integrationName}`
    : undefined;

  return (
    <Form
      control={control}
      watch={watch}
      formState={formState}
      handleSubmit={handleSubmit}
      {...form}
    >
      <form
        onSubmit={handleSubmit((data) => handlesSubmitForm(data as T))}
        onReset={handleCancelForm}
        className={"space-y-4"}
      >
        <FormField
          render={({ field }) => (
            <FormItem>
              <FormLabel>Container Name</FormLabel>
              <FormControl>
                <Input
                  disabled={formState.isSubmitting}
                  placeholder="Enter a new container"
                  {...field}
                  className="text-primary"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
          name={"displayName"}
          control={control}
        />
        {isEditing ? (
          <div className="flex flex-col gap-5">
            {employees && (
              <ContainerLinkGroup
                icon={IconNames.users}
                title={terminolize(
                  "Linked Technicians",
                  TerminologyOptions.technician,
                  "plural"
                )}
                showSyncColumn={showTechnicianSync}
                rows={technicianRows}
                emptyText={`No ${terminolize("technicians", TerminologyOptions.technician, "plural").toLowerCase()} linked yet.`}
                addOptions={technicianAddOptions}
                addPlaceholder={`Add a ${terminolize("technician", TerminologyOptions.technician, "singular").toLowerCase()}…`}
                addSearchPlaceholder={`Search ${terminolize("technicians", TerminologyOptions.technician, "plural").toLowerCase()}…`}
                addEmptyText={`No matching ${terminolize("technician", TerminologyOptions.technician, "singular").toLowerCase()}`}
                onAddSelect={handleAddTechnician}
              />
            )}
            {labels && (
              <ContainerLinkGroup
                icon={IconNames.tags}
                title="Linked Source Labels"
                showSyncColumn={showLabelSync}
                rows={labelRows}
                emptyText="No source labels linked yet."
                addOptions={labelAddOptions}
                addPlaceholder="Add a source label…"
                addSearchPlaceholder="Search source labels…"
                addEmptyText="No matching source label"
                onAddSelect={handleAddLabel}
              />
            )}
          </div>
        ) : (
          <div className={"grid grid-cols-2 gap-4"}>
            {employees && (
              <div className="flex flex-col gap-2">
                <Label>
                  Linked{" "}
                  {terminolize(
                    "Technicians",
                    TerminologyOptions.technician,
                    "plural"
                  )}
                </Label>
                {fields.map((field, index) => (
                  <div
                    className="group flex flex-row items-center justify-between gap-4 rounded p-2 hover:bg-accent/20"
                    key={field.id}
                  >
                    <Select
                      onValueChange={(value) =>
                        handleUpdateTechnician(index, value)
                      }
                      value={watchedTechnicianIds[index] || ""}
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue
                          placeholder={
                            "Pick a " +
                            terminolize(
                              "Technician",
                              TerminologyOptions.technician,
                              "singular"
                            )
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {employees
                          .filter((emp) => !emp.hideInShopHero)
                          .map((emp) => (
                            <SelectItem key={emp.id} value={`${emp.id}`}>
                              {emp.firstName} {emp.lastName}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    {showTechnicianSync && technicianWritebackEnabled && (
                      <Tooltip
                        message={`Mark as a technician written back to ${integrationName} on a manual card move`}
                      >
                        <div className="flex flex-row items-center gap-2">
                          <Switch
                            checked={
                              !!watchedTechnicianIds[index] &&
                              watchedBidirectionalTechnicianIds.includes(
                                watchedTechnicianIds[index]
                              )
                            }
                            disabled={!watchedTechnicianIds[index]}
                            onCheckedChange={(checked) =>
                              handleToggleBidirectionalTechnician(
                                watchedTechnicianIds[index],
                                checked
                              )
                            }
                          />
                          <Label className="text-xs font-normal">
                            Bidirectional
                          </Label>
                        </div>
                      </Tooltip>
                    )}
                    <Tooltip
                      message={`Remove ${getEmployeeName(watchedTechnicianIds[index], employees)}`}
                    >
                      <Button
                        size={"smIcon"}
                        onClick={() => handleRemoveTechnician(index)}
                        type={"button"}
                      >
                        <X />
                      </Button>
                    </Tooltip>
                  </div>
                ))}
                <div className="flex w-full flex-row items-center gap-4">
                  <Button
                    size={"smIcon"}
                    onClick={() => append("")}
                    type={"button"}
                    disabled={hasEmptySelection}
                  >
                    <PlusIcon />
                  </Button>
                  <span>
                    Click to add {fields.length > 0 ? "another" : "a"}{" "}
                    {terminolize(
                      "technician",
                      TerminologyOptions.technician,
                      "singular"
                    )}
                  </span>
                </div>
              </div>
            )}
            {labels && (
              <div className="flex flex-col gap-2">
                <Label>Linked Source Labels</Label>
                {linkedSourceLabelField.map((field, index) => (
                  <div
                    className="group flex flex-row items-center justify-between gap-4 rounded p-2 hover:bg-accent/20"
                    key={field.id}
                  >
                    <Select
                      onValueChange={(value) =>
                        handleUpdateSourceLabel(index, value)
                      }
                      value={watchedSourceLabels[index] || ""}
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="Pick a Label" />
                      </SelectTrigger>
                      <SelectContent>
                        {labels
                          .filter(
                            (label) =>
                              !watchedSourceLabels.includes(label) ||
                              label === watchedSourceLabels[index]
                          )
                          .map((label) => (
                            <SelectItem key={label} value={`${label}`}>
                              {label}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    {isROLabelWritebackEnabled && (
                      <Tooltip
                        message={
                          labelHasCatalogMatch(
                            watchedSourceLabels[index],
                            labelCatalog
                          )
                            ? "Mark as the label written back to Tekmetric on a manual card move"
                            : "No matching Tekmetric label found — this label can't sync back until it's re-linked to a current Tekmetric label"
                        }
                      >
                        <div className="flex flex-row items-center gap-2">
                          <Switch
                            checked={
                              !!watchedSourceLabels[index] &&
                              watchedBidirectionalLabels.includes(
                                watchedSourceLabels[index]
                              )
                            }
                            disabled={
                              !watchedSourceLabels[index] ||
                              !labelHasCatalogMatch(
                                watchedSourceLabels[index],
                                labelCatalog
                              )
                            }
                            onCheckedChange={(checked) =>
                              handleToggleBidirectional(
                                watchedSourceLabels[index],
                                checked
                              )
                            }
                          />
                          <Label className="text-xs font-normal">
                            Bidirectional
                          </Label>
                        </div>
                      </Tooltip>
                    )}
                    <Tooltip message={`Remove ${watchedSourceLabels[index]}`}>
                      <Button
                        size={"smIcon"}
                        onClick={() => handleRemoveSourceLabel(index)}
                        type={"button"}
                      >
                        <X />
                      </Button>
                    </Tooltip>
                  </div>
                ))}
                <div className="flex w-full flex-row items-center gap-4">
                  <Button
                    size={"smIcon"}
                    onClick={() => linkedSourceLabelAppend("")}
                    type={"button"}
                    disabled={hasEmptySourceLabelSelection}
                  >
                    <PlusIcon />
                  </Button>
                  <span>
                    Click to add{" "}
                    {linkedSourceLabelField.length > 0 ? "another" : "a"} label
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
        {isEditing && syncSummaryText && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Icon name={IconNames.bidirectional} size={14} />
            <span>{syncSummaryText}</span>
          </div>
        )}
        <div className="flex flex-row justify-end gap-4">
          <Button type="reset" variant="destructive" disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? (
              <Spinner size={"sm"} />
            ) : isEditing ? (
              "Save changes"
            ) : (
              "Submit"
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};
